import React, { useState, useEffect } from 'react';
import './AdminPanel.css';
import { getAdminStats, getAllUsers, updateUserAdminStatus, deleteUser } from '../../services/admin.service';

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, usersData] = await Promise.all([
        getAdminStats(),
        getAllUsers()
      ]);
      setStats(statsData);
      setUsers(usersData);
    } catch (err) {
      console.error('Error loading admin data:', err);
      if (err.response?.status === 403) {
        setError('Access denied. You do not have admin privileges.');
      } else {
        setError('Failed to load admin data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, currentStatus, userEmail) => {
    if (window.confirm(`Are you sure you want to ${currentStatus ? 'remove admin privileges from' : 'grant admin privileges to'} ${userEmail}?`)) {
      try {
        const domain = userEmail.split('@')[1];
        await updateUserAdminStatus(userId, !currentStatus, !currentStatus ? domain : null);
        await loadData();
      } catch (err) {
        console.error('Error updating admin status:', err);
        alert(err.response?.data?.detail || 'Failed to update admin status');
      }
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await deleteUser(selectedUser.user_id);
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const confirmDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const handleToggleAffiliate = async (userId, currentlyAffiliated, userEmail) => {
    const action = currentlyAffiliated ? 'remove affiliation from' : 'affiliate';
    if (window.confirm(`Are you sure you want to ${action} ${userEmail} with UR?`)) {
      try {
        const user = JSON.parse(localStorage.getItem('user'));
        const token = user?.access_token;

        const method = currentlyAffiliated ? 'DELETE' : 'POST';
        const response = await fetch(
          `https://campuslens.xyz/api/admin/users/${userId}/affiliate`,
          {
            method: method,
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          await loadData();
        } else {
          const error = await response.json();
          alert(error.detail || 'Failed to update affiliation');
        }
      } catch (err) {
        console.error('Error updating affiliation:', err);
        alert('Failed to update affiliation');
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.fullname && user.fullname.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'admin' && user.is_admin) ||
      (filterType === 'regular' && !user.is_admin) ||
      (filterType === 'verified' && user.is_verified) ||
      (filterType === 'unverified' && !user.is_verified);

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1>Admin Dashboard</h1>
            <p className="admin-subtitle">Manage users in your domain</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <a
              href="/admin-setup"
              style={{
                background: '#4caf50', color: '#fff', padding: '10px 20px',
                borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem',
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              📷 Camera Setup
            </a>
            <a
              href="/admin-data"
              style={{
                background: '#2196f3', color: '#fff', padding: '10px 20px',
                borderRadius: '8px', fontWeight: '600', fontSize: '0.9rem',
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              📊 Analytics & Export
            </a>
          </div>
        </div>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>{stats.total_users}</h3>
              <p>Total Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✓</div>
            <div className="stat-content">
              <h3>{stats.verified_users}</h3>
              <p>Verified Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔑</div>
            <div className="stat-content">
              <h3>{stats.admin_users}</h3>
              <p>Admin Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🏢</div>
            <div className="stat-content">
              <h3>{stats.domain_users}</h3>
              <p>Domain Users</p>
            </div>
          </div>
        </div>
      )}

      <div className="user-management">
        <div className="controls-bar">
          <input
            type="text"
            className="search-input"
            placeholder="Search users by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filterType === 'admin' ? 'active' : ''}`}
              onClick={() => setFilterType('admin')}
            >
              Admins
            </button>
            <button
              className={`filter-btn ${filterType === 'regular' ? 'active' : ''}`}
              onClick={() => setFilterType('regular')}
            >
              Regular
            </button>
            <button
              className={`filter-btn ${filterType === 'verified' ? 'active' : ''}`}
              onClick={() => setFilterType('verified')}
            >
              Verified
            </button>
            <button
              className={`filter-btn ${filterType === 'unverified' ? 'active' : ''}`}
              onClick={() => setFilterType('unverified')}
            >
              Unverified
            </button>
          </div>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Role</th>
                <th>UR Affiliate</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-results">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.user_id}>
                    <td className="email-cell">{user.email}</td>
                    <td>{user.fullname || '-'}</td>
                    <td>
                      <span className={`provider-badge ${user.provider}`}>
                        {user.provider}
                      </span>
                    </td>
                    <td>
                      {user.is_verified !== null && (
                        <span className={`status-badge ${user.is_verified ? 'verified' : 'unverified'}`}>
                          {user.is_verified ? 'Verified' : 'Unverified'}
                        </span>
                      )}
                      {user.is_verified === null && (
                        <span className="status-badge verified">Active</span>
                      )}
                    </td>
                    <td>
                      <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                        {user.is_admin ? 'Admin' : 'User'}
                      </span>
                      {user.admin_domain && (
                        <div className="admin-domain-tag">{user.admin_domain}</div>
                      )}
                    </td>
                    <td>
                      {user.affiliate_domain ? (
                        <span className="status-badge verified">
                          ✓ {user.affiliate_domain}
                        </span>
                      ) : user.email.toLowerCase().endsWith('@u.rochester.edu') || user.email.toLowerCase().endsWith('@rochester.edu') ? (
                        <span className="status-badge verified">
                          ✓ UR Email
                        </span>
                      ) : (
                        <span className="status-badge unverified">
                          No
                        </span>
                      )}
                    </td>
                    <td className="date-cell">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <button
                        className={`action-btn ${user.is_admin ? 'danger' : 'success'}`}
                        onClick={() => handleToggleAdmin(user.user_id, user.is_admin, user.email)}
                        title={user.is_admin ? 'Remove Admin' : 'Make Admin'}
                      >
                        {user.is_admin ? '🔓' : '🔑'}
                      </button>
                      <button
                        className={`action-btn ${user.affiliate_domain ? 'danger' : 'success'}`}
                        onClick={() => handleToggleAffiliate(user.user_id, user.affiliate_domain, user.email)}
                        title={user.affiliate_domain ? 'Remove UR Affiliation' : 'Affiliate with UR'}
                      >
                        {user.affiliate_domain ? '🏫' : '➕'}
                      </button>
                      <button
                        className="action-btn danger"
                        onClick={() => confirmDelete(user)}
                        title="Delete User"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDeleteConfirm && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Confirm Deletion</h2>
            <p>
              Are you sure you want to delete the user <strong>{selectedUser.email}</strong>?
            </p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                }}
              >
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={handleDeleteUser}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
